import * as React from 'react';

import * as cn from 'classnames';
import { Controller, FieldError, useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import { Alert, Autocomplete, Button, ButtonContainer, Checkbox, Grid, Spacer, TextField, Tooltip, Typography } from '@emburse/ui-core';

import { USPSAddressVerificationModal } from '@ReactComponents/PersonalSettings/Components/USPSAddressVerificationModal/USPSAddressVerificationModal';
import { PhoneNumberInput } from '@ReactComponents/PhoneNumberInput/PhoneNumberInput';
import { SignUpTerms } from '@ReactComponents/SignUp/SignUpTerms/SignUpTerms';
import { SignUpWorkflowFooter } from '@ReactComponents/SignUp/SignUpWorkflowFooter';

import { StoreCompany } from '@Actions/company';
import { UpdateUserProfile } from '@Actions/currentUserActions';
import { IAppStateContainer } from '@ReactStore/reducers';

import { CompanyApi } from '@ClientApi/company';
import * as addressHelper from '@Shared/helpers/address';
import { IIsoCountry, IIsoState } from '@Shared/interfaces';
import { ICompanyModel } from '@Shared/models';
import { AddressSuggestionForUser, UserInputAddress } from '@Shared/types/user_address';

import * as style from './CompanyInformationForm.styl';

export interface ICompanyForm {
  name: string;
  street_address: string;
  address_city: string;
  address_state: Partial<IIsoState & IIsoCountry>;
  country: string;
  address_country_code: string;
  postal_code: string;
  phone_number: string;
  website: string;
  employees_with_expenses_range?: string;
  tos_accepted?: boolean;
  ein?: string;
}
interface ICompanyInformationForm {
  onCompanyInfoSubmit?: (companyData: Partial<ICompanyModel & { company_id: string }>) => void;
  onCompanyInfoSubmitNewSignup?: (navDirection: 'next' | 'back') => void;
  company: ICompanyModel;
  isAdditionalCompany?: boolean;
}

const urlRegEx =
  /^(?:(?:https):\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,}))\.?)(?::\d{2,5})?(?:[/?#]\S*)?$/i;

const showOnboardUserButtons = window.location.pathname === '/onboardUser';

const CompanyInformationForm: React.FC<ICompanyInformationForm> = ({
  onCompanyInfoSubmit,
  onCompanyInfoSubmitNewSignup,
  company,
  isAdditionalCompany
}) => {
  const dispatch = useDispatch();
  const country = addressHelper.getCountryByThreeLetterCode(company.address_country_code || 'USA');
  const states = country ? addressHelper.getIsoStatesForCountryByTwoLetterCode(country.iso3166alpha2) : [];
  const { control, handleSubmit, getValues, setValue } = useFormContext<ICompanyForm>();
  const [uspsAddressDiff, setUspsAddressDiff] = React.useState<Record<string, string>>(null);
  const [currentAddress, setCurrentAddress] = React.useState<UserInputAddress>(null);
  const [uspsReturnText, setUspsReturnText] = React.useState<string>(null);
  const [validationErrorMessage, setValidationErrorMessage] = React.useState<string>('');
  const employeesWithExpensesRange = ['1 to 50', '51 to 100', '101 to 250', '250+'];
  const companyData = useSelector((state: IAppStateContainer) => state.company.company) as any;
  const ariaLabelBase = 'company-info';
  const [skipWebsite, setSkipWebsite] = React.useState<boolean>(company.website === '');
  async function updateCompanyForNewSignup(value: ICompanyForm) {
    try {
      setValidationErrorMessage('');

      let response;
      if (isAdditionalCompany && !company.id) {
        response = await CompanyApi.sendAdditionalCompanyData({
          id: company.id,
          ...value,
          address_state: value.address_state.subdivision_name
        });

        if (response.updatedCompany.id) {
          window.companyId = response.updatedCompany.id || window.companyId;
          window.ORG = response.updatedCompany;
          window.USER_PROFILE = response.userProfile;
          dispatch(new UpdateUserProfile(response.userProfile));
        }
      } else {
        response = await CompanyApi.sendWelcomeCompanyData({
          id: company.id,
          ...value,
          address_state: value.address_state.subdivision_name
        });
      }

      response.updatedCompany.utm_parameter = companyData.utm_parameter;
      window.companyId = response.updatedCompany.id || window.companyId;
      dispatch(new StoreCompany(response.updatedCompany));
      const verifyAddressResult = response.verifyAddressResult;
      const addressDiff = verifyAddressResult?.addressDiff || {};
      if (Object.keys(addressDiff).length > 0) {
        handleAddressSuggestion(addressDiff, verifyAddressResult.returnText);
      } else {
        onCompanyInfoSubmitNewSignup('next');
      }
    } catch (error) {
      let errorMessage;
      try {
        const data = JSON.parse(error.message);
        errorMessage = data.error;
      } catch (e) {
        errorMessage = error.message;
      }
      setValidationErrorMessage(errorMessage);
    }
  }

  const onSubmit = async (value: ICompanyForm) => {
    // TODO: this needs to be cleaned up
    if (!onCompanyInfoSubmitNewSignup) {
      if (isAdditionalCompany) {
        const response = await CompanyApi.sendAdditionalCompanyData({
          id: company.id,
          ...value,
          address_state: value.address_state.subdivision_name
        });
        if (response.company) {
          window.companyId = response.company.id || window.companyId;
          window.ORG = response.company;
          window.USER_PROFILE = response.userProfile;
          dispatch(new UpdateUserProfile(response.userProfile));
        }
        window.location.href = `/home?company_id=${response.company.id}`;
      } else {
        return onCompanyInfoSubmit({
          ...value,
          street_address: value.street_address,
          postal_code: value.postal_code,
          address_city: value.address_city,
          address_state: value.address_state.subdivision_name,
          address_country_code: country.iso3166alpha3,
          currency_code: 'USD',
          company_id: company.id,
          id: company.id
        });
      }
    } else if (isAdditionalCompany || company.state === 'pending') {
      return updateCompanyForNewSignup(value);
    }
  };

  function handleAddressSuggestion(addressDiff: Record<string, string>, returnText: string) {
    const addressState = getValues('address_state');
    const currentUserAddressData: UserInputAddress = {
      address_state: addressState.subdivision_name,
      address_city: getValues('address_city'),
      street_address: getValues('street_address'),
      postal_code: getValues('postal_code')
    };

    setUspsAddressDiff(addressDiff);
    if (returnText) {
      setUspsReturnText(returnText);
    }
    setCurrentAddress(currentUserAddressData);
  }

  async function handleAcceptAddressChanges(suggestionData: AddressSuggestionForUser) {
    for (const [key, obj] of Object.entries(suggestionData)) {
      if (obj.changed) {
        if (key === 'address_state') {
          // usps is weird, address validation for florida fails and will return FL
          const oldValue = getValues('address_state');
          let newValue: IIsoState = states.find((option) => option.us_state_code === obj.value);
          if (oldValue.subdivision_name === newValue.subdivision_name) {
            // the state is the same but usps is unhappy with the full state name
            newValue = {
              ...newValue,
              subdivision_name: obj.value
            };
          }
          setValue('address_state', newValue);
        } else {
          setValue(key as 'street_address' | 'address_city' | 'postal_code', obj.value);
        }
      }
    }

    await handleSubmit(onSubmit)();
    handleCloseAddressSuggestion();
  }

  function handleCloseAddressSuggestion() {
    setCurrentAddress(null);
    setUspsAddressDiff(null);
    setUspsReturnText(null);
  }

  return (
    <Grid container direction="column" spacing={1} sx={{ height: '100%', maxWidth: '500px' }} wrap="nowrap" className={style.global}>
      <Grid item className={cn(`${(isAdditionalCompany || company.state === 'pending') && 'center-info'}`)}>
        <Grid item style={{ width: '100%' }}>
          <Typography variant="h4">Company Information</Typography>
          {validationErrorMessage && (
            <Grid item>
              <Spacer size={25} />
              <Alert severity="error" className="alert">
                {validationErrorMessage}
              </Alert>
            </Grid>
          )}
          <Spacer size={25} />
          <Controller
            rules={{ required: 'Field is required' }}
            control={control}
            name="name"
            render={({ formState: { errors }, field }) => (
              <TextField
                {...field}
                label="Legally Registered Business Name"
                fullWidth
                error={errors.name}
                helperText={errors.name?.message}
              />
            )}
          />
          <Spacer size={25} />
          {company?.whitelabel_configuration_id === 'SVB' && (
            <>
              <Grid item style={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }}>
                <Controller
                  rules={{
                    required: 'Field is required',
                    pattern: { value: /\d\d-?\d\d\d\d\d\d\d/, message: 'This field is required and must contain 9 digits' }
                  }}
                  name="ein"
                  control={control}
                  render={({ formState: { errors }, field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Employer Identification Number (EIN)"
                      required
                      error={errors.ein}
                      helperText={errors.ein?.message}
                    />
                  )}
                />
              </Grid>
              <Spacer size={25} />
            </>
          )}
          <Controller
            rules={{ required: 'Field is required' }}
            control={control}
            name="street_address"
            render={({ formState: { errors }, field }) => (
              <TextField
                {...field}
                label="Street Address"
                fullWidth
                error={errors.street_address}
                helperText={errors.street_address?.message}
              />
            )}
          />
          <Spacer size={25} />
          <Grid container alignItems="flex-start" wrap="nowrap" spacing={2}>
            <Grid item xs={6}>
              <Controller
                rules={{ required: 'Field is required' }}
                control={control}
                name="address_city"
                render={({ formState: { errors }, field }) => (
                  <TextField {...field} label="City" fullWidth error={errors.address_city} helperText={errors.address_city?.message} />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <Controller
                rules={{
                  required: 'Field is required',
                  validate: (value) => (value.country_code || value?.subdivision_name ? true : 'Field is required')
                }}
                control={control}
                name="address_state"
                render={({ formState: { errors }, field }) => (
                  <Autocomplete
                    {...field}
                    id="company-information-form-address-state"
                    InputProps={{ id: 'company-information-form-address-state' }}
                    value={field.value}
                    label="State / Province"
                    options={states}
                    getOptionLabel={(option: IIsoState) => option.subdivision_name}
                    error={errors.address_state}
                    helperText={(errors.address_state as FieldError)?.message}
                    onChange={(_: any, city: any) => field.onChange(city)}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Spacer size={25} />
          <Grid container alignItems="flex-start" wrap="nowrap" spacing={2}>
            <Grid item sm={6} xs={6}>
              <Controller
                rules={{
                  required: 'Field is required',
                  minLength: { message: 'Must be at least 5 digits', value: 5 },
                  maxLength: { message: 'Must be under 10 digits number', value: 10 }
                }}
                control={control}
                name="postal_code"
                render={({ formState: { errors }, field }) => (
                  <TextField
                    {...field}
                    label="Zip / Postal Code"
                    fullWidth
                    error={errors.postal_code}
                    helperText={errors.postal_code?.message}
                  />
                )}
              />
            </Grid>
            <Grid item sm={6} xs={6}>
              <Controller
                rules={{
                  required: 'Field is required',
                  validate: (value) => {
                    return value.length > 10 || 'Invalid phone number';
                  }
                }}
                control={control}
                name="phone_number"
                render={({ formState: { errors }, field }) => (
                  <PhoneNumberInput
                    defaultCountry={'us'}
                    disableCountryDropdown
                    preferredCountries={['us']}
                    inputProps={{ name: 'Phone Number', required: true }}
                    label="Phone Number *"
                    value={field.value}
                    onChange={field.onChange}
                    errorMessage={errors.phone_number?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
          <Spacer size={25} />
          <Controller
            rules={{
              required: !skipWebsite && 'Field is required',
              pattern: skipWebsite ? undefined : { value: urlRegEx, message: 'Url is invalid' }
            }}
            control={control}
            name="website"
            render={({ formState: { errors }, field }) => (
              <>
                <TextField
                  {...field}
                  label="Website"
                  placeholder="https://example.com"
                  fullWidth
                  onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                    if (e.target instanceof HTMLInputElement) {
                      e.target.value = e.target.value.trim();
                      if (e.target.value.indexOf('/') < 0) {
                        e.target.value = 'https://' + e.target.value;
                        return field.onChange(e);
                      }
                    }
                    field.onBlur();
                  }}
                  error={errors.website}
                  helperText={errors.website?.message}
                  disabled={skipWebsite}
                />
                <Tooltip
                  title="Only check this box if your company does not have an official website. If you have any online presence—even a temporary or social media page—please enter the URL instead."
                  arrow
                  placement="right"
                >
                  <label>
                    <Checkbox
                      checked={skipWebsite}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setSkipWebsite(e.target.checked);
                        if (e.target.checked) {
                          field.onChange('');
                        }
                      }}
                    />
                    Skip Website
                  </label>
                </Tooltip>
              </>
            )}
          />
          {(isAdditionalCompany || company.state === 'pending') && (
            <>
              <Spacer size={25} />
              <Controller
                rules={{
                  required: 'Field is required'
                }}
                control={control}
                name="employees_with_expenses_range"
                render={({ formState: { errors }, field }) => (
                  <Autocomplete
                    {...field}
                    id="company-information-form-employee-count"
                    InputProps={{ id: 'company-information-form-employee-count' }}
                    value={field.value}
                    label="Employee Count"
                    options={employeesWithExpensesRange}
                    error={errors.employees_with_expenses_range}
                    helperText={(errors.employees_with_expenses_range as FieldError)?.message}
                    onChange={(_: any, option: any) => field.onChange(option)}
                  />
                )}
              />
            </>
          )}
          {isAdditionalCompany && (
            <>
              <Spacer size={25} />
              <Controller
                rules={{
                  required: 'Field is required'
                }}
                control={control}
                name="tos_accepted"
                render={({ formState: { errors }, field: { onChange, value } }) => (
                  <SignUpTerms
                    checked={value}
                    error={Boolean(errors.tos_accepted)}
                    onChange={(newVal) => onChange(newVal)}
                    whitelabelName={window.whitelabelConfiguration?.tags?.brandName}
                  />
                )}
              />
            </>
          )}
          <Spacer size={25} />
          {company.state !== 'pending' && showOnboardUserButtons && (
            <ButtonContainer alignButtons="right">
              <Button variant="outlined" disabled className={`${ariaLabelBase}-back`}>
                Back
              </Button>
              <Button onClick={handleSubmit(onSubmit)} className={`${ariaLabelBase}-next`}>
                Next
              </Button>
            </ButtonContainer>
          )}
        </Grid>
        {Boolean(currentAddress) && (
          <USPSAddressVerificationModal
            currentUserInputAddress={currentAddress}
            uspsReturnText={uspsReturnText}
            uspsAddressDiff={uspsAddressDiff}
            isOpen={Boolean(currentAddress)}
            onClose={handleCloseAddressSuggestion}
            onAcceptChanges={handleAcceptAddressChanges}
          />
        )}
      </Grid>
      {(isAdditionalCompany || company.state === 'pending' || company?.whitelabel_configuration_id === 'SVB') && (
        <Grid item container xs alignContent="flex-end">
          <SignUpWorkflowFooter onNext={handleSubmit(onSubmit)} showSupportLink={true} ariaLabel={ariaLabelBase} />
        </Grid>
      )}
    </Grid>
  );
};

export { CompanyInformationForm };
